import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Check, Search } from 'lucide-react'
import type { FilterOutput } from '@/components/queryBuilder/types'
import { filterRows } from '@/components/queryBuilder/utils/evaluateFilters'
import DeferredQueryBuilderUsage from '@/components/playground/DeferredQueryBuilderUsage'
import { DemoPageHero, DemoStage } from '@/components/editorial/DemoPageLayout'
import SectionHeader from '@/components/editorial/SectionHeader'
import ApplicationEnquiry from '@/components/editorial/ApplicationEnquiry'
import Reveal from '@/components/preview/Reveal'
import {
  createQueryEmployeeRows,
  createQueryProductRows,
} from '@/lib/queryBuilderDemoData'

const QueryBuilderWidget = lazy(
  () => import('@/components/playground/QueryBuilderWidget'),
)

export const Route = createFileRoute('/playground/querybuilder')({
  component: QueryBuilderPage,
  head: () => {
    const url = 'https://www.suriyaprakash.in/playground/querybuilder'
    const title = 'React Visual QueryBuilder Demo | Suriyaprakash Somu'
    const description =
      'Try a React visual filter builder with quick filters and nested AND/OR conditions. Explore schema-driven filtering with live results from sample business records.'
    return {
      meta: [
        { title },
        { name: 'title', content: title },
        { name: 'description', content: description },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        { name: 'geo.placename', content: 'Erode, Tamil Nadu, India' },
        {
          name: 'keywords',
          content:
            'QueryBuilder, filter builder, Zod schema filters, advanced query, AND OR groups, filter chips, live filtering, filter engine, seeded dataset, TypeScript React components, Suriyaprakash Somu',
        },
        {
          name: 'ai-summary',
          content:
            'This page demonstrates a schema-driven React QueryBuilder that maps Zod schemas to typed filter fields, builds quick chip filters and nested AND/OR groups, and evaluates every edit live against 300 deterministic records with a JSON payload preview.',
        },
        { property: 'og:title', content: title },
        { property: 'og:image:alt', content: title },
        { name: 'twitter:image:alt', content: title },
        { property: 'og:description', content: description },
        { property: 'og:locale', content: 'en_IN' },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content:
            'https://www.suriyaprakash.in/og/playground-querybuilder.png',
        },
        { property: 'og:url', content: url },
        { property: 'og:site_name', content: 'Suriyaprakash Somu' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        {
          name: 'twitter:image',
          content:
            'https://www.suriyaprakash.in/og/playground-querybuilder.png',
        },
      ],
      links: [
        { rel: 'canonical', href: url },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: '/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'QueryBuilder Playground',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Web',
            url,
            description,
            creator: {
              '@type': 'Person',
              '@id': 'https://www.suriyaprakash.in/#person',
              url: 'https://www.suriyaprakash.in/',
              name: 'Suriyaprakash Somu',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Erode',
                addressRegion: 'Tamil Nadu',
                addressCountry: 'IN',
              },
            },
            featureList: [
              'Zod schema to typed filter fields',
              'Quick chip filters and advanced AND/OR groups',
              'Type-aware operators per field',
              'Live filtering over seeded records',
              'Live results table with match percentage',
              'JSON filter payload preview',
            ],
            keywords: ['TypeScript', 'React', 'Zod'],
          }),
        },
      ],
    }
  },
})

const ROLES = ['Developer', 'Designer', 'Manager', 'Analyst'] as const
const DEPARTMENTS = [
  'Engineering',
  'Design',
  'Sales',
  'Marketing',
  'Support',
] as const
const STATUSES = ['Active', 'Inactive', 'Pending'] as const

interface EmployeeRow {
  id: number
  name: string
  email: string
  role: (typeof ROLES)[number]
  department: (typeof DEPARTMENTS)[number]
  status: (typeof STATUSES)[number]
  salary: number
  joinedAt: string
  isActive: boolean
  [key: string]: unknown
}

interface ProductRow {
  id: number
  product: string
  category: 'Electronics' | 'Apparel' | 'Books' | 'Home'
  price: number
  inStock: boolean
  createdAt: string
  quantity: number
  [key: string]: unknown
}

const STATUS_BADGES: Record<string, string> = {
  Active: 'border-emerald-700/20 bg-emerald-50 text-emerald-800',
  Inactive: 'border-rose-700/20 bg-rose-50 text-rose-800',
  Pending: 'border-amber-700/25 bg-amber-50 text-amber-900',
}

function StatusBadge({ value }: { value: string }) {
  const color = STATUS_BADGES[value] ?? 'border-line bg-cream-100 text-ink-soft'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${color}`}
    >
      {value}
    </span>
  )
}

function StockBadge({ inStock }: { inStock: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${
        inStock
          ? 'border-emerald-700/20 bg-emerald-50 text-emerald-800'
          : 'border-rose-700/20 bg-rose-50 text-rose-800'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${inStock ? 'bg-emerald-500' : 'bg-rose-400'}`}
      />
      {inStock ? 'In stock' : 'Out'}
    </span>
  )
}

function QueryBuilderPage() {
  const demoRef = useRef<HTMLDivElement>(null)
  const [showDemo, setShowDemo] = useState(false)
  const [selectedSchema, setSelectedSchema] = useState<'employee' | 'product'>(
    'employee',
  )
  const [liveFilters, setLiveFilters] = useState<
    (FilterOutput & { search: string }) | null
  >(null)

  const employees = useMemo(
    () =>
      createQueryEmployeeRows(300, 4200).map((row, index) => ({
        ...row,
        role: ROLES[index % ROLES.length],
        department: DEPARTMENTS[index % DEPARTMENTS.length],
        status: STATUSES[index % STATUSES.length],
      })) satisfies Array<EmployeeRow>,
    [],
  )
  const products = useMemo(
    () => createQueryProductRows(300, 9100) satisfies Array<ProductRow>,
    [],
  )
  const rows = selectedSchema === 'employee' ? employees : products

  const matchedRows = useMemo(
    () =>
      filterRows<EmployeeRow | ProductRow>(
        rows,
        liveFilters,
        liveFilters?.search ?? '',
      ),
    [rows, liveFilters],
  )
  const matchPct =
    rows.length > 0 ? Math.round((matchedRows.length / rows.length) * 100) : 0
  const isFiltered = liveFilters !== null
  const previewRows = matchedRows.slice(0, 6)
  const conditionCount = liveFilters?.conditions.length ?? 0

  useEffect(() => {
    const element = demoRef.current
    if (!element) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.2) {
          setShowDemo(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <DemoPageHero
        system="SYS.03"
        eyebrow="React QueryBuilder · Schema-driven filters"
        title="React QueryBuilder."
        accent="Find records that matter."
        description="Combine conditions to explore employee or product data without writing a query. Try quick filters or nested AND/OR groups; results update against 300 generated records in your browser, not a live business database."
        specs={[
          { label: 'Mode', value: 'Chips + Tree' },
          { label: 'Operators', value: 'Per type' },
          { label: 'Dataset', value: '300 seeded' },
          { label: 'Results', value: 'Live' },
        ]}
      />

      <DemoStage
        system="SYS.03"
        hint="Start with quick filter chips for simple cases, or switch to Advanced to nest AND/OR groups. Results and the filter payload update live."
      >
        <div className="overflow-hidden rounded-[1.25rem] border border-ink/20 bg-cream-50">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/15 bg-sage-50/70 px-4 py-4 sm:px-6">
            <span className="preview-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted">
              Demo schema
            </span>
            <div className="inline-flex max-w-full rounded-full border border-ink/20 bg-cream-50 p-1">
              <button
                onClick={() => setSelectedSchema('employee')}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${selectedSchema === 'employee' ? 'bg-forest text-white' : 'text-ink-soft hover:bg-sage-50/70 hover:text-forest'}`}
              >
                Employee
              </button>
              <button
                onClick={() => setSelectedSchema('product')}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${selectedSchema === 'product' ? 'bg-forest text-white' : 'text-ink-soft hover:bg-sage-50/70 hover:text-forest'}`}
              >
                Product
              </button>
            </div>
          </div>

          <div ref={demoRef} className="min-h-[28rem] min-w-0 p-2 sm:p-4">
            {showDemo ? (
              <Suspense fallback={<QueryBuilderPlaceholder />}>
                <QueryBuilderWidget
                  selectedSchema={selectedSchema}
                  onChange={setLiveFilters}
                />
              </Suspense>
            ) : (
              <QueryBuilderPlaceholder />
            )}
          </div>
        </div>

        <div className="mt-4 grid min-w-0 gap-4 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col overflow-hidden rounded-[1.25rem] border border-ink/20 bg-cream-50 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="preview-mono text-xs font-bold uppercase tracking-[0.14em] text-ink">
                  Live results
                </h2>
                <p className="mt-0.5 text-xs text-muted">
                  {isFiltered
                    ? `${matchedRows.length} of ${rows.length} seeded records match`
                    : `All ${rows.length} seeded records — add a filter to narrow`}
                </p>
              </div>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                  isFiltered && matchedRows.length === 0
                    ? 'bg-rose-50 text-rose-700'
                    : isFiltered
                      ? 'bg-forest text-white'
                      : 'bg-sage-100 text-forest'
                }`}
              >
                {matchPct}% match
              </span>
            </div>

            <div
              className="mt-3 h-1.5 overflow-hidden rounded-full bg-sage-100"
              role="presentation"
            >
              <div
                className={`h-full rounded-full transition-all duration-300 ${isFiltered && matchedRows.length === 0 ? 'bg-rose-400' : 'bg-forest'}`}
                style={{ width: `${matchPct}%` }}
              />
            </div>

            {matchedRows.length === 0 ? (
              <div className="mt-4 flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-line bg-cream-50/60 px-4 py-10 text-center">
                <Search className="h-5 w-5 text-muted" />
                <p className="mt-2 text-sm font-semibold text-ink">
                  No records match
                </p>
                <p className="mt-1 max-w-xs text-xs leading-5 text-muted">
                  Your filters are too strict for this dataset. Loosen a value
                  or clear a condition.
                </p>
              </div>
            ) : (
              <div className="mt-4 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                <table className="w-full min-w-[560px] border-collapse text-left text-[13px] sm:min-w-[520px]">
                  <thead>
                    <tr className="border-b border-line/70 text-[10px] font-bold uppercase tracking-[0.12em] text-muted">
                      {selectedSchema === 'employee' ? (
                        <>
                          <th className="py-2 pr-3 font-bold">Employee</th>
                          <th className="py-2 pr-3 font-bold">Role</th>
                          <th className="py-2 pr-3 font-bold">Department</th>
                          <th className="py-2 pr-3 font-bold">Status</th>
                          <th className="py-2 text-right font-bold">Salary</th>
                        </>
                      ) : (
                        <>
                          <th className="py-2 pr-3 font-bold">Product</th>
                          <th className="py-2 pr-3 font-bold">Category</th>
                          <th className="py-2 pr-3 font-bold">Stock</th>
                          <th className="py-2 pr-3 text-right font-bold">
                            Price
                          </th>
                          <th className="py-2 text-right font-bold">Qty</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSchema === 'employee'
                      ? (previewRows as Array<EmployeeRow>).map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-line/40 last:border-0"
                          >
                            <td className="py-2 pr-3">
                              <p className="font-semibold leading-tight text-ink">
                                {row.name}
                              </p>
                              <p className="text-[11px] leading-tight text-muted">
                                {row.email}
                              </p>
                            </td>
                            <td className="py-2 pr-3 text-ink-soft">
                              {row.role}
                            </td>
                            <td className="py-2 pr-3 text-ink-soft">
                              {row.department}
                            </td>
                            <td className="py-2 pr-3">
                              <StatusBadge value={row.status} />
                            </td>
                            <td className="py-2 text-right font-mono tabular-nums text-ink">
                              ${row.salary.toLocaleString('en-US')}
                            </td>
                          </tr>
                        ))
                      : (previewRows as Array<ProductRow>).map((row) => (
                          <tr
                            key={row.id}
                            className="border-b border-line/40 last:border-0"
                          >
                            <td className="py-2 pr-3 font-semibold text-ink">
                              {row.product}
                            </td>
                            <td className="py-2 pr-3 text-ink-soft">
                              {row.category}
                            </td>
                            <td className="py-2 pr-3">
                              <StockBadge inStock={row.inStock} />
                            </td>
                            <td className="py-2 pr-3 text-right font-mono tabular-nums text-ink">
                              $
                              {row.price.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                              })}
                            </td>
                            <td className="py-2 text-right font-mono tabular-nums text-ink-soft">
                              {row.quantity}
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-auto pt-3 text-[11px] leading-5 text-muted">
              Showing {Math.min(6, matchedRows.length)} of {matchedRows.length}{' '}
              matches · evaluated live on every edit · payload below is what
              you’d send as{' '}
              <code className="rounded bg-cream-100 px-1 font-mono text-[10px]">
                queryParams.filters
              </code>
            </p>
          </div>

          <div className="flex min-w-0 flex-col overflow-hidden rounded-[1.25rem] border border-ink/20 bg-cream-50 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="preview-mono text-xs font-bold uppercase tracking-[0.14em] text-ink">
                Filter payload
              </h2>
              <span className="shrink-0 rounded-full bg-sage-100 px-2.5 py-1 text-[10px] font-bold text-forest">
                {isFiltered
                  ? `${conditionCount} condition${conditionCount !== 1 ? 's' : ''}${liveFilters?.search ? ' + search' : ''}`
                  : 'no filters'}
              </span>
            </div>
            <pre className="mt-3 max-h-[320px] w-full max-w-full overflow-auto rounded-xl bg-[#101915] p-3 text-xs leading-5 text-sage-100 sm:max-h-[420px] sm:p-4">
              <code className="break-all sm:break-normal">
                {JSON.stringify(
                  liveFilters ?? { logic: 'AND', conditions: [], search: '' },
                  null,
                  2,
                )}
              </code>
            </pre>
            <div className="mt-3 rounded-xl border border-dashed border-forest/20 bg-sage-50/60 p-3 text-xs leading-5 text-muted">
              An application can connect{' '}
              <code className="rounded bg-cream-100 px-1 font-mono text-[10px]">
                fetchData
              </code>{' '}
              to an API that validates and translates this filter payload. This
              demo evaluates filters locally; it is not connected to the
              separate DataTable demo.
            </div>
          </div>
        </div>

        <div className="mt-4 grid border-y border-ink/15 text-sm text-ink-soft md:grid-cols-3">
          <p className="border-ink/15 py-5 md:border-r md:px-5">
            <strong className="text-ink">Quick filters:</strong> one labelled
            control per field, with active filters listed above as removable
            chips. Good for simple, everyday searches.
          </p>
          <p className="border-t border-ink/15 py-5 md:border-r md:border-t-0 md:px-5">
            <strong className="text-ink">Advanced:</strong> AND/OR rail per
            group, add condition or inner group (max depth 2). Field change
            resets the operator.
          </p>
          <p className="border-t border-ink/15 py-5 md:border-t-0 md:px-5">
            <strong className="text-ink">Live engine:</strong> every operator
            from the schema runs client-side against seeded rows — deterministic
            between server and browser.
          </p>
        </div>
      </DemoStage>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="What"
            title="What is a schema-driven"
            accent="QueryBuilder?"
          />
          <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:gap-16 sm:mt-16">
            <Reveal>
              <div className="space-y-4 text-base leading-7 text-ink-soft sm:leading-8">
                <p>
                  A schema-driven QueryBuilder is a visual filter composer whose
                  fields, input types, and operators are derived from a{' '}
                  <strong className="font-semibold text-ink">Zod schema</strong>{' '}
                  instead of being hand-written per screen. The schema is the
                  single source of truth: add a field to the schema and the
                  builder picks it up with the right input control and operator
                  set — no filter UI code to maintain.
                </p>
                <p>
                  This playground is a working demonstration of that pattern.
                  Both demo schemas (employee and product) are plain Zod objects
                  with field metadata; the builder turns them into typed filter
                  fields, and a small evaluation engine runs the resulting query
                  against seeded records in real time.
                </p>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="border-t border-ink/20 pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
                <h2 className="font-heading text-4xl tracking-[-0.03em] text-ink sm:text-5xl">
                  How it works
                </h2>
                <div className="mt-6 space-y-4 text-base leading-7 text-ink-soft sm:leading-8">
                  <p>
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      zodToFilterFields
                    </code>{' '}
                    maps each schema key to a{' '}
                    <strong className="font-semibold text-ink">
                      FilterField
                    </strong>{' '}
                    — type, label, and allowed operators. Quick mode renders one
                    chip per field with a fixed operator; advanced mode renders
                    a tree of conditions and nested AND/OR groups (max depth 2).
                    Every edit emits a JSON payload shaped like{' '}
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      {'{ logic, conditions, search }'}
                    </code>{' '}
                    — a payload an application can pass through{' '}
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      queryParams.filters
                    </code>
                    .
                  </p>
                  <p>
                    The payload feeds a pure evaluation engine (
                    <code className="rounded bg-cream-100 px-1.5 py-0.5 text-sm font-semibold text-ink">
                      evaluateFilters.ts
                    </code>
                    ) that implements every operator — text contains, number
                    comparisons, boolean flags, multiselect includes, date
                    before/after/between — and recurses through groups. The 300
                    demo rows are generated with per-row{' '}
                    <strong className="font-semibold text-ink">
                      deterministic seeds
                    </strong>
                    , so server and browser evaluate against identical data.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 text-cream-50 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            tone="dark"
            eyebrow="Integration"
            title="Usage"
            description="The schema declares the fields. QueryBuilder emits the filter contract."
          />
          <Reveal
            delay={100}
            className="mt-12 sm:mt-16 [&>div]:border-white/15 [&>div]:shadow-none"
          >
            <DeferredQueryBuilderUsage
              code={`import { z } from 'zod'
import { QueryBuilder } from '@/components/queryBuilder/QueryBuilder'

const schema = z.object({
  name: z.string().describe(JSON.stringify({ label: 'Name' })),
  status: z.enum(['Active','Inactive']).describe(JSON.stringify({
    label: 'Status', inputType: 'select',
    options: [{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]
  })),
  salary: z.coerce.number().describe(JSON.stringify({ label: 'Salary' })),
  joinedAt: z.string().describe(JSON.stringify({ label: 'Joined', inputType: 'date' })),
})

export default function FilterPanel() {
  return (
    <QueryBuilder
      schema={schema}
      onQueryChange={(filters) => console.log(filters)}
      defaultOpen
      ui={{ showModeToggle: true, persistMode: false }}
    />
  )
}`}
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-cream-50 py-20 text-ink sm:py-28">
        <div className="container-custom">
          <SectionHeader eyebrow="Capabilities" title="Features" />
          <div className="mt-12 border-b border-ink/20 sm:mt-16">
            {[
              'Zod schema mapped to typed filter fields',
              'Quick chip filters and advanced AND/OR groups',
              'Type-aware operators per field',
              'Live filtering over 300 seeded records',
              'Live results table with match percentage',
              'JSON payload preview matching the DataTable contract',
            ].map((feature, index) => (
              <Reveal key={feature} delay={index * 50}>
                <div className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 border-t border-ink/20 py-5 sm:grid-cols-[4rem_1fr_auto] sm:py-7">
                  <span className="preview-mono text-[10px] font-bold text-muted">
                    0{index + 1}
                  </span>
                  <p className="text-sm font-semibold text-ink-soft sm:text-base">
                    {feature}
                  </p>
                  <Check className="h-4 w-4 text-forest" />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 sm:mt-24">
            <div className="grid gap-8 border-y border-ink/20 py-8 lg:grid-cols-[minmax(16rem,0.7fr)_minmax(0,1.3fr)] lg:gap-16 lg:py-12">
              <h2 className="font-heading text-4xl leading-none tracking-[-0.03em] text-ink sm:text-5xl">
                When to use a component like this
              </h2>
              <div className="space-y-4 text-base leading-7 text-ink-soft sm:leading-8">
                <p>
                  A QueryBuilder earns its place when users need to compose
                  their own filters — admin panels, ERP list screens, CRM
                  segments, support queues, audit logs. Deriving the builder
                  from a schema keeps field definitions reusable. A backend
                  integration still needs to validate allowed fields and
                  operators, enforce permissions and translate the payload into
                  database queries.
                </p>
                <p>
                  Built with React, TypeScript and Zod. Related demos:{' '}
                  <Link
                    to="/playground/datatable"
                    className="font-semibold text-forest underline decoration-forest/30 underline-offset-2 hover:decoration-forest"
                  >
                    the React DataTable playground
                  </Link>
                  , which demonstrates the record-management interface, and{' '}
                  <Link
                    to="/playground/schemaform"
                    className="font-semibold text-forest underline decoration-forest/30 underline-offset-2 hover:decoration-forest"
                  >
                    the SchemaForm builder
                  </Link>
                  , which uses the same schema-driven pattern for data entry.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <ApplicationEnquiry />
    </>
  )
}

function QueryBuilderPlaceholder() {
  return (
    <div className="grid min-h-[27rem] place-items-center rounded-xl border border-ink/15 bg-cream-100/60">
      <p className="preview-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        Loading interactive filters…
      </p>
    </div>
  )
}
